import React from 'react';
import {
  MdCheck,
  MdErrorOutline,
  MdPlayArrow,
  MdStop,
} from 'react-icons/md';
import { IconButton } from '@aibuild/ui';
import { useNodeRun } from '../../hooks/useNodeRun';
import { gateTooltip } from '../runStatus/upstreamGating';
import { nodeWarningTooltip } from '../validation/nodeWarningTooltip';
import {
  HeaderRunButtonShell,
  RunningIconStack,
  SpinnerRing,
} from './NodeHeaderRunButton.styled';

export interface NodeHeaderRunButtonProps {
  /** ID of the node this header button belongs to. */
  readonly nodeId: string;
}

/**
 * Per-node Run icon-button rendered inside the {@code NodeCardShell}
 * header. Mirrors the design's `.ns-node__run-btn` four-state cycle:
 *
 * <ul>
 *   <li><b>idle</b> — purple {@code play_arrow}; clicking starts the run.</li>
 *   <li><b>running</b> — neutral {@code stop} glyph ringed by a rotating
 *       arc (deliberate deviation from the design, which renders a bare
 *       spinner). The glyph telegraphs "click to cancel"; the arc
 *       telegraphs "still in flight". The card's outline pulse via
 *       {@code NODE_CARD_STATE_RUNNING} already conveys live activity
 *       at the card scope.</li>
 *   <li><b>completed (live)</b> — green {@code check}; clicking re-runs.
 *       Persisted-but-completed nodes fall back to the idle play glyph
 *       so the canvas doesn't permanently remember "this ran once" with
 *       a green button forever.</li>
 *   <li><b>warning</b> — red {@code error_outline}; disabled, with the
 *       actionable tooltip from {@link nodeWarningTooltip}. Wins over
 *       <i>idle</i>; <i>running</i> still wins over warning so the user
 *       can always cancel an in-flight run.</li>
 * </ul>
 *
 * <p>This is the editor's primary execution affordance for a single
 * node — there is intentionally no other "Run" surface inside the
 * card. The footer Run+Stop pair from earlier iterations was removed
 * because it duplicated this affordance and ate the vertical space the
 * design devotes to per-output port pills.</p>
 */
const NodeHeaderRunButton: React.FC<NodeHeaderRunButtonProps> = ({
  nodeId,
}) => {
  const { state, gate, errors, hasWarning, isRunning, start, cancel } =
    useNodeRun(nodeId);

  if (isRunning) {
    return (
      <HeaderRunButtonShell $tone="running">
        <IconButton
          ariaLabel="Stop run"
          tooltip="Cancel this run."
          icon={
            <RunningIconStack
              data-testid={`process-node__header-stop-${nodeId}__stack`}
            >
              <SpinnerRing
                aria-hidden="true"
                data-testid={`process-node__header-stop-${nodeId}__spinner`}
              />
              <MdStop />
            </RunningIconStack>
          }
          size="sm"
          variant="ghost"
          onClick={cancel}
          dataTestId={`process-node__header-stop-${nodeId}`}
        />
      </HeaderRunButtonShell>
    );
  }

  if (hasWarning) {
    const warningTooltip = nodeWarningTooltip(errors);
    return (
      <HeaderRunButtonShell $tone="warning">
        <IconButton
          ariaLabel="Check this node"
          tooltip={warningTooltip ?? 'This node has validation errors.'}
          icon={<MdErrorOutline />}
          size="sm"
          variant="ghost"
          disabled
          dataTestId={`process-node__header-check-${nodeId}`}
        />
      </HeaderRunButtonShell>
    );
  }

  // The bright "freshly finished" green check is reserved for the live
  // run snapshot — once the persisted snapshot replaces the live one,
  // the button falls back to the idle play glyph so an old run from a
  // previous session doesn't permanently mark the node as "done".
  const justFinished = state.source === 'live' && state.status === 'completed';
  if (justFinished) {
    return (
      <HeaderRunButtonShell $tone="done">
        <IconButton
          ariaLabel="Re-run this node"
          tooltip="Computed — click to re-run."
          icon={<MdCheck />}
          size="sm"
          variant="ghost"
          disabled={!gate.canRun}
          onClick={start}
          dataTestId={`process-node__header-rerun-${nodeId}`}
        />
      </HeaderRunButtonShell>
    );
  }

  // Hide the affordance entirely when the gate considers the node
  // "never runnable" (read-only canvas, decorative kind). Validation
  // errors keep it visible-but-warning above; upstream-pending /
  // run_in_progress keep it visible-but-disabled with the tooltip
  // explaining why.
  if (
    !gate.canRun &&
    (gate.reason === 'read_only' || gate.reason === 'not_runnable')
  ) {
    return null;
  }

  return (
    <HeaderRunButtonShell $tone="idle">
      <IconButton
        ariaLabel="Run this node"
        tooltip={gateTooltip(gate)}
        icon={<MdPlayArrow />}
        size="sm"
        variant="ghost"
        disabled={!gate.canRun}
        onClick={start}
        dataTestId={`process-node__header-run-${nodeId}`}
      />
    </HeaderRunButtonShell>
  );
};

export default NodeHeaderRunButton;
